import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  IExpense,
  IExpenseCategory,
  IExpenseItem,
  IExpenseRequest
} from '../../models/expense.model';
import { ISibasiHTTPResponse } from 'src/app/common/model/sibasiHTTPResponse.model';
import { IPagination } from 'src/app/common/model/util.model';

@Injectable({
  providedIn: 'root'
})
export class ExpenditureService {
  constructor(private http: HttpClient) {}

  public getExpenses(): Observable<ISibasiHTTPResponse<IExpense[]>> {
    return this.http.get<ISibasiHTTPResponse<IExpense[]>>(
      `${environment.api_url}expense`
    );
  }
  public getExpenseById(expenseId: string): Observable<ISibasiHTTPResponse<IExpense>> {
    return this.http.get<ISibasiHTTPResponse<IExpense>>(
      `${environment.api_url}expense/${expenseId}`
    );
  }

  public createExpense(expense: IExpense): Observable<ISibasiHTTPResponse<IExpense>> {
    return this.http.post<ISibasiHTTPResponse<IExpense>>(
      `${environment.api_url}expense`,
      expense
    );
  }

  public deleteExpenseId(expenseId: string) {
    return this.http.delete(`${environment.api_url}expense/${expenseId}`);
  }

  public updateExpense(
    expense: IExpense,
    expenseId: string | undefined
  ): Observable<ISibasiHTTPResponse<IExpense>> {
    return this.http.post<ISibasiHTTPResponse<IExpense>>(
      `${environment.api_url}expense/${expenseId}`,
      expense
    );
  }
  getAllExpensesCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}expense/all/count`
    );
  }

  getSearchedExpensesCount(searchParam: string): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}expense/all/paginated/count`,
      { searchParam }
    );
  }

  getPaginatedExpenses(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<any>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<any>>>(
      `${environment.api_url}expense/all/paginated`,
      pagination ? pagination : {}
    );
  }

  getCategoryFilteredExpenses(
    categoryId: string,
    pagination?: any
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.http
      .post<
        ISibasiHTTPResponse<any>
      >(`${environment.api_url}expense/paginated/category/${categoryId}`, pagination ? pagination : {})
      .pipe(
        map((res) => {
          const expenses = res.result.expenses;
          return {
            ...res,
            result: expenses
          };
        })
      );
  }

  /* ######################################## EXPENDITURE CATEGORY ######################################## */

  public createCategory(
    category: IExpenseCategory
  ): Observable<ISibasiHTTPResponse<IExpenseCategory>> {
    return this.http.post<ISibasiHTTPResponse<IExpenseCategory>>(
      `${environment.api_url}pos/expense/category`,
      category
    );
  }

  public updateCategory(
    category: IExpenseCategory,
    categoryId: string | undefined
  ): Observable<ISibasiHTTPResponse<IExpenseCategory>> {
    return this.http.post<ISibasiHTTPResponse<IExpenseCategory>>(
      `${environment.api_url}pos/expense/category/${categoryId}`,
      category
    );
  }

  public getAllCategories(): Observable<ISibasiHTTPResponse<IExpenseCategory[]>> {
    return this.http.get<ISibasiHTTPResponse<IExpenseCategory[]>>(
      `${environment.api_url}pos/expense/category`
    );
  }
  public getPaginatedCategories(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IExpenseCategory[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<IExpenseCategory[]>>>(
      `${environment.api_url}pos/expense/category/all/paginated`,
      pagination
    );
  }

  public getCategoryById(
    categoryId: string
  ): Observable<ISibasiHTTPResponse<IExpenseCategory>> {
    return this.http.get<ISibasiHTTPResponse<IExpenseCategory>>(
      `${environment.api_url}pos/expense/category/${categoryId}`
    );
  }

  public deleteCategory(
    categoryId: string | undefined
  ): Observable<ISibasiHTTPResponse<IExpenseCategory>> {
    return this.http.delete<ISibasiHTTPResponse<IExpenseCategory>>(
      `${environment.api_url}pos/expense/category/${categoryId}`
    );
  }

  /* ######################################## EXPENDITURE ITEM ######################################## */

  public createExpenditureItem(
    expenditureItem: Partial<IExpenseItem>
  ): Observable<ISibasiHTTPResponse<IExpenseItem>> {
    return this.http.post<ISibasiHTTPResponse<IExpenseItem>>(
      `${environment.api_url}expenditure/items`,
      expenditureItem
    );
  }

  public updateExpenditureItem(
    expenditureItem: Partial<IExpenseItem>,
    expenditureItemId: string | undefined
  ): Observable<ISibasiHTTPResponse<IExpenseItem>> {
    return this.http.post<ISibasiHTTPResponse<IExpenseItem>>(
      `${environment.api_url}expenditure/items/${expenditureItemId}`,
      expenditureItem
    );
  }

  public getAllExpenditureItems(): Observable<ISibasiHTTPResponse<IExpenseItem[]>> {
    return this.http.get<ISibasiHTTPResponse<IExpenseItem[]>>(
      `${environment.api_url}expenditure/items`
    );
  }

  public getExpenditureItemById(
    expenditureItemId: string
  ): Observable<ISibasiHTTPResponse<IExpenseItem>> {
    return this.http.get<ISibasiHTTPResponse<IExpenseItem>>(
      `${environment.api_url}expenditure/items/${expenditureItemId}`
    );
  }

  public deleteExpenditureItem(
    expenditureItemId: string | undefined
  ): Observable<ISibasiHTTPResponse<IExpenseItem>> {
    return this.http.delete<ISibasiHTTPResponse<IExpenseItem>>(
      `${environment.api_url}expenditure/items/${expenditureItemId}`
    );
  }

  public getExpenseReports(dateRange: any): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}expense/aggregated/category`,
      dateRange
    );
  }
  /* ######################################## EXPENSE REQUEST ######################################## */

  public createExpenseRequest(
    expenditureItem: Partial<IExpenseRequest>
  ): Observable<ISibasiHTTPResponse<IExpenseRequest>> {
    return this.http.post<ISibasiHTTPResponse<IExpenseRequest>>(
      `${environment.api_url}expense-request`,
      expenditureItem
    );
  }

  public updateExpenseRequest(
    expenseRequest: Partial<IExpenseRequest>,
    expenseRequestId: string | undefined
  ): Observable<ISibasiHTTPResponse<IExpenseRequest>> {
    return this.http.post<ISibasiHTTPResponse<IExpenseRequest>>(
      `${environment.api_url}expense-request/update/${expenseRequestId}`,
      expenseRequest
    );
  }

  public getAllExpenseRequests(): Observable<ISibasiHTTPResponse<IExpenseRequest[]>> {
    return this.http.get<ISibasiHTTPResponse<IExpenseRequest[]>>(
      `${environment.api_url}expense-request`
    );
  }

  public getPaginatedExpenseRequests(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IExpenseCategory[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<IExpenseCategory[]>>>(
      `${environment.api_url}expense-request/all/paginated`,
      pagination
    );
  }

  getAllExpenseRequestCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}expense-request/all/count`
    );
  }

  getSearchedExpenseRequestCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}expense-request/all/paginated/count`,
      { searchParam }
    );
  }

  public getExpenseRequestById(
    expenditureItemId: string
  ): Observable<ISibasiHTTPResponse<IExpenseRequest>> {
    return this.http.get<ISibasiHTTPResponse<IExpenseRequest>>(
      `${environment.api_url}expense-request/${expenditureItemId}`
    );
  }

  public deleteExpenseRequest(
    expenditureItemId: string | undefined
  ): Observable<ISibasiHTTPResponse<IExpenseRequest>> {
    return this.http.delete<ISibasiHTTPResponse<IExpenseRequest>>(
      `${environment.api_url}expense-request/${expenditureItemId}`
    );
  }

  approveExpenseRequest(expenseRequestId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.http.get<ISibasiHTTPResponse<any>>(
      `${environment.api_url}expense-request/${expenseRequestId}/approve`
    );
  }
  disburseExpenseRequest(expenseRequestId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.http.get<ISibasiHTTPResponse<any>>(
      `${environment.api_url}expense-request/${expenseRequestId}/disburse`
    );
  }
}
